import { useCallback, useMemo, useState, type ReactNode } from "react";
import { VideoFocusContext } from "./videoFocus";

/** Minimum visible fraction before a phone frame can win focus. */
const FOCUS_THRESHOLD = 0.25;

/**
 * Tracks how much of each phone frame is on screen so that only the most
 * visible one plays its video at a time.
 */
export function VideoFocusProvider({ children }: { children: ReactNode }) {
  const [visibility, setVisibilityMap] = useState<Record<string, number>>({});

  const setVisibility = useCallback((id: string, ratio: number) => {
    setVisibilityMap((current) => {
      if (ratio === 0) {
        if (!(id in current)) return current;
        const next = { ...current };
        delete next[id];
        return next;
      }
      if (current[id] === ratio) return current;
      return { ...current, [id]: ratio };
    });
  }, []);

  const focusedId = useMemo(() => {
    let bestId: string | null = null;
    let bestRatio = FOCUS_THRESHOLD;

    for (const [id, ratio] of Object.entries(visibility)) {
      if (ratio > bestRatio) {
        bestRatio = ratio;
        bestId = id;
      }
    }

    return bestId;
  }, [visibility]);

  const value = useMemo(
    () => ({ focusedId, setVisibility }),
    [focusedId, setVisibility],
  );

  return (
    <VideoFocusContext.Provider value={value}>
      {children}
    </VideoFocusContext.Provider>
  );
}
